// @ts-nocheck
import { Dispatch } from 'redux';
import { workstate } from 'src/actions';
import { URLAAlternateNameActionType } from 'src/components/Loan/Application/Sections/BorrowerInfo/Inputs/AlternateNames/store/types';
import { ResidenceActionType } from 'src/components/Loan/Application/Sections/BorrowerInfo/Inputs/Residence/store/types';
import { AssetActionType } from 'src/components/Loan/Application/Sections/AssetsLiabilitiesInfo/Inputs/Assets/store/types';
import { GiftGrantActionType } from '../../Sections/AssetsLiabilitiesInfo/Inputs/GiftsGrants/store/types';

import {
  ApplicationActionType,
  LoanActionType,
} from 'src/components/Loan/Application/store/actionTypes';
import { BorrowerOrCoBorrowerType, Guid } from 'src/types';
import { Borrower } from 'src/types/borrower';
import { Loan } from 'src/types/loan';
import { apiAxios, hasAnyChanges } from 'src/util';
import { OtherAssetActionType } from 'src/components/Loan/Application/Sections/AssetsLiabilitiesInfo/Inputs/OtherAssets/store/types';
import { LiabilityActionType } from 'src/components/Loan/Application/Sections/AssetsLiabilitiesInfo/Inputs/Liabilities/store/types';
import { OtherLiabilityActionType } from 'src/components/Loan/Application/Sections/AssetsLiabilitiesInfo/Inputs/OtherLiabilities/store/types';
import { OtherIncomeActionType } from 'src/components/Loan/Application/Sections/IncomeInfo/Inputs/OtherIncomes/store/types';
import { EmploymentActionType } from 'src/components/Loan/Application/Sections/EmploymentInfo/Inputs/Employments/store/types';
import { FieldActionType } from 'src/components/Loan/Application/store/fields/actions';
import { ReoPropertyActionType } from 'src/components/Loan/Application/Sections/AssetsLiabilitiesInfo/Inputs/Properties/store/types';

// Simple queue to prevent concurrent updates to the same loan
const updateQueues = new Map<string, Promise<any>>();

export const clearLoan = () => ({
  type: 'clear_loan_data',
});

const setResidences = (
  borrower: Borrower,
  applicantType: BorrowerOrCoBorrowerType,
) => ({
  type: ResidenceActionType.SET,
  data: borrower.residences
    .map((residence) => ({
      ...residence,
      owner: applicantType,
    }))
    .filter((x) => !x.id?.startsWith('MailingAddress')),
});

const setAlternateNames = (
  borrower: Borrower,
  applicantType: BorrowerOrCoBorrowerType,
) => ({
  type: URLAAlternateNameActionType.SET,
  data: borrower.urlaAlternateNames.map((name) => ({
    ...name,
    owner: applicantType,
  })),
});

const setEmployments = (
  borrower: Borrower,
  applicantType: BorrowerOrCoBorrowerType,
) => ({
  type: EmploymentActionType.SET,
  data: borrower.employment.map((employment) => ({
    ...employment,
    owner: applicantType,
  })),
});

export const getNewLoanData = (loanGuid: Guid) => (dispatch: Dispatch) => {
  return workstate(dispatch, 'fetch-loan-new', async () => {
    const res = await apiAxios.get(`loans/${loanGuid}`);

    const loan: Loan = res.data;

    const applications = loan.applications;
    const currentApplication = applications[0];

    if (currentApplication?.borrower?.urlaAlternateNames) {
      dispatch(
        setAlternateNames(
          currentApplication.borrower,
          BorrowerOrCoBorrowerType.Borrower,
        ),
      );
    }
    if (currentApplication?.coBorrower?.urlaAlternateNames) {
      dispatch(
        setAlternateNames(
          currentApplication.coBorrower,
          BorrowerOrCoBorrowerType.CoBorrower,
        ),
      );
    }

    if (currentApplication?.borrower?.residences) {
      dispatch(
        setResidences(
          currentApplication.borrower,
          BorrowerOrCoBorrowerType.Borrower,
        ),
      );
    }
    if (currentApplication?.coBorrower?.residences) {
      dispatch(
        setResidences(
          currentApplication.coBorrower,
          BorrowerOrCoBorrowerType.CoBorrower,
        ),
      );
    }

    if (currentApplication?.borrower?.employment) {
      dispatch(
        setEmployments(
          currentApplication.borrower,
          BorrowerOrCoBorrowerType.Borrower,
        ),
      );
    }
    if (currentApplication?.coBorrower?.employment) {
      dispatch(
        setEmployments(
          currentApplication.coBorrower,
          BorrowerOrCoBorrowerType.CoBorrower,
        ),
      );
    }

    if (currentApplication?.vods) {
      dispatch({
        type: AssetActionType.SET,
        data: currentApplication.vods,
      });
    }

    if (currentApplication?.otherAssets) {
      dispatch({
        type: OtherAssetActionType.SET,
        data: currentApplication.otherAssets,
      });
    }

    if (currentApplication?.giftsGrants) {
      dispatch({
        type: GiftGrantActionType.SET,
        data: currentApplication.giftsGrants,
      });
    }

    if (currentApplication?.vols) {
      dispatch({
        type: LiabilityActionType.SET,
        data: currentApplication.vols,
      });
    }

    if (currentApplication?.otherLiabilities) {
      dispatch({
        type: OtherLiabilityActionType.SET,
        data: currentApplication.otherLiabilities,
      });
    }

    if (currentApplication?.otherIncomeSources) {
      dispatch({
        type: OtherIncomeActionType.SET,
        data: currentApplication.otherIncomeSources,
      });
    }

    if (loan.customFields) {
      dispatch({
        type: FieldActionType.SET,
        data: loan.customFields,
      });
    }

    if (currentApplication?.reoProperties) {
      dispatch({
        type: ReoPropertyActionType.SET,
        data: currentApplication.reoProperties,
      });
    }

    // tidy up collections u shouldnt be touching directly in the loan, there are separate stores for these
    if (applications) {
      for (const i in applications) {
        delete applications[i].otherAssets;
        delete applications[i].vods;
        delete applications[i].vols;
        delete applications[i].reoProperties;
        delete applications[i].giftsGrants;
        delete applications[i].otherLiabilities;

        delete applications[i].borrower?.urlaAlternateNames;
        delete applications[i].borrower?.residences;
        delete applications[i].coBorrower?.urlaAlternateNames;
        delete applications[i].coBorrower?.residences;
      }
      dispatch({
        type: ApplicationActionType.SET,
        data: applications,
      });
    }

    delete loan.applications;
    delete loan.customFields;

    dispatch({
      type: LoanActionType.UPDATE,
      data: loan,
    });
  });
};

export const updateLoan =
  (loanGuid: Guid, data: Loan) => (dispatch: Dispatch, getState) => {
    return workstate(dispatch, 'update-loan', async () => {
      if (!hasAnyChanges(data)) return;

      // Wait for any existing update to complete for this loan to prevent conflicts
      const existingUpdate = updateQueues.get(loanGuid);
      if (existingUpdate) {
        try {
          await existingUpdate;
        } catch (error) {
          // Ignore errors from previous updates
        }
      }

      const updatePromise = performUpdate();
      updateQueues.set(loanGuid, updatePromise);

      try {
        const result = await updatePromise;
        return result;
      } finally {
        // Clean up the queue entry
        if (updateQueues.get(loanGuid) === updatePromise) {
          updateQueues.delete(loanGuid);
        }
      }

      async function performUpdate () {
        const maxRetries = 3;
        let attempt = 0;

        while (attempt < maxRetries) {
          try {
            const lockId = getState().app.lockId;

            dispatch({
              type: LoanActionType.UPDATE,
              data: data,
            });

            // Construct the URL with lockId parameter manually
            let url = `loans/${loanGuid}`;
            if (lockId != null && lockId !== '') {
              url += `?lockId=${encodeURIComponent(lockId)}`;
            }

            const res = await apiAxios.patch<Loan>(url, data);

            const loan = res.data;
            delete loan.applications;

            // Only update store with server response if there are meaningful differences
            // This prevents flickering when server returns the same data we just sent
            const currentState = getState();
            const currentLoan = currentState.loan?.loan;

            // Check if server returned any new/different data that we should apply
            const serverOnlyFields = {};
            let hasServerUpdates = false;

            // Look for fields that came back from server but weren't in our update
            for (const [key, value] of Object.entries(loan)) {
              if (!(key in data)) {
                // This field wasn't in our update, so it's potentially new from server
                if (
                  JSON.stringify(value) !== JSON.stringify(currentLoan?.[key])
                ) {
                  serverOnlyFields[key] = value;
                  hasServerUpdates = true;
                }
              }
            }

            // Only dispatch update if server provided new information
            if (hasServerUpdates) {
              dispatch({
                type: LoanActionType.UPDATE,
                data: serverOnlyFields,
              });
            }

            if (loan?.property?.addressLineText !== undefined) {
              dispatch({
                type: 'update_borrower_loan_application',
                data: {
                  loanGuid,
                  propertyName: loan.property.addressLineText,
                },
              });
            }
            if (loan?.baseLoanAmount !== undefined) {
              dispatch({
                type: 'update_borrower_loan_application',
                data: {
                  loanGuid,
                  loanAmount: loan.baseLoanAmount,
                },
              });
            }

            // Success - break out of retry loop
            return;
          } catch (error) {
            attempt++;

            // Licensing rejections are permanent business-rule failures, not
            // transient lock conflicts — retrying can never succeed
            const isPermanentConflict = error.response?.data?.messages?.[0]?.includes(
              'not licensed to originate loans',
            );

            // If it's a 409 conflict and we have retries left, refresh loan data and retry
            if (
              error.response?.status === 409 &&
              !isPermanentConflict &&
              attempt < maxRetries
            ) {
              console.warn(
                `Update conflict (409) on attempt ${attempt}, retrying after refreshing loan data...`,
              );

              // Wait a brief moment before retrying to avoid immediate conflicts
              const delay = 100 * attempt;
              await new Promise((resolve) => setTimeout(resolve, delay));

              // Refresh the loan data to get the latest version
              try {
                await dispatch(getNewLoanData(loanGuid));
              } catch (refreshError) {
                console.warn(
                  'Failed to refresh loan data for retry:',
                  refreshError,
                );
              }

              continue; // Try again with fresh data
            }

            // If it's not a 409 or we've exhausted retries, re-throw the error
            throw error;
          }
        }
      }
    });
  };
